import React from 'react';
import { QuestionStep, FormResponses } from '@/types/solidarity';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { getQuestionData } from '@/utils/questionData';

interface QuestionRendererProps {
  question: QuestionStep;
  responses: FormResponses;
  onUpdate: (section: keyof FormResponses, key: string, value: any) => void;
}

export const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  responses,
  onUpdate
}) => {
  const questionData = getQuestionData(question.questionKey);
  
  if (!questionData) {
    return <div>Chyba: Otázka nenalezena</div>;
  }

  const currentValue = responses[question.section][question.questionKey as keyof typeof responses[typeof question.section]];

  const handleRadioChange = (value: string) => {
    onUpdate(question.section, question.questionKey, parseInt(value));
  };

  const handleCheckboxChange = (key: string, checked: boolean) => {
    // Handle mutual exclusivity for Q04 and D17
    if (question.questionKey === 'Q04') {
      if (key === 'Q04_D' && checked) {
        // If "Nic z výše uvedeného" is selected, clear others
        onUpdate(question.section, 'Q04_A', false);
        onUpdate(question.section, 'Q04_B', false);
        onUpdate(question.section, 'Q04_C', false);
        onUpdate(question.section, 'Q04_D', true);
      } else if (key !== 'Q04_D' && checked) {
        // If any other option is selected, clear "Nic z výše uvedeného"
        onUpdate(question.section, 'Q04_D', false);
        onUpdate(question.section, key, checked);
      } else {
        onUpdate(question.section, key, checked);
      }
    } else if (question.questionKey === 'D17') {
      if (key === 'D17_F' && checked) {
        // If "Nic z výše uvedeného" is selected, clear others
        onUpdate(question.section, 'D17_A', false);
        onUpdate(question.section, 'D17_B', false);
        onUpdate(question.section, 'D17_C', false);
        onUpdate(question.section, 'D17_D', false);
        onUpdate(question.section, 'D17_E', false);
        onUpdate(question.section, 'D17_F', true);
      } else if (key !== 'D17_F' && checked) {
        // If any other option is selected, clear "Nic z výše uvedeného"
        onUpdate(question.section, 'D17_F', false);
        onUpdate(question.section, key, checked);
      } else {
        onUpdate(question.section, key, checked);
      }
    } else {
      onUpdate(question.section, key, checked);
    }
  };

  const handleNumberChange = (value: string) => {
    const numValue = parseInt(value) || 0;
    if (numValue >= 0 && numValue <= 700) {
      onUpdate(question.section, question.questionKey, numValue);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg sm:text-xl font-semibold text-slate-800 mb-4 leading-tight">
          {questionData.title}
        </h3>
        {questionData.description && (
          <p className="text-slate-600 mb-4 text-sm sm:text-base leading-relaxed">
            {questionData.description}
          </p>
        )}
      </div>

      {questionData.type === 'radio' && (
        <RadioGroup 
          value={currentValue !== undefined ? currentValue.toString() : ""} 
          onValueChange={handleRadioChange}
          className="space-y-2"
        >
          {questionData.options?.map((option) => (
            <Label 
              key={option.value} 
              htmlFor={`${question.questionKey}-${option.value}`}
              className="flex items-start space-x-3 p-3 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all duration-200 cursor-pointer active:bg-slate-100 w-full"
            >
              <RadioGroupItem 
                value={option.value.toString()} 
                id={`${question.questionKey}-${option.value}`}
                className="text-indigo-600 mt-1 flex-shrink-0"
              />
              <span className="text-slate-700 flex-1 text-sm sm:text-base leading-relaxed select-none">
                {option.label}
              </span>
            </Label>
          ))}
        </RadioGroup>
      )}

      {questionData.type === 'checkbox' && (
        <div className="space-y-2">
          {questionData.options?.map((option) => {
            const isChecked = responses[question.section][option.key as keyof typeof responses[typeof question.section]] as boolean;
            return (
              <Label 
                key={option.key} 
                htmlFor={`${question.questionKey}-${option.key}`}
                className="flex items-start space-x-3 p-3 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all duration-200 cursor-pointer active:bg-slate-100 w-full"
              >
                <Checkbox
                  id={`${question.questionKey}-${option.key}`}
                  checked={isChecked || false}
                  onCheckedChange={(checked) => handleCheckboxChange(option.key, checked as boolean)}
                  className="mt-1 flex-shrink-0"
                />
                <span className="text-slate-700 flex-1 text-sm sm:text-base leading-relaxed select-none">
                  {option.label}
                </span>
              </Label>
            );
          })}
        </div>
      )}

      {questionData.type === 'number' && (
        <div className="space-y-3">
          <Input
            type="number"
            min="0"
            max="700"
            value={currentValue?.toString() || ''}
            onChange={(e) => handleNumberChange(e.target.value)}
            placeholder="Zadejte počet hodin"
            className="w-full text-base sm:text-lg p-3 rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
          />
          <p className="text-sm text-slate-500">
            Zadejte celé číslo od 0 do 700
          </p>
        </div>
      )}
    </div>
  );
};